/**
 * Supply chain panel — disruption monitoring across ports, cables,
 * pipelines, and waterways.
 *
 * Aggregates data from infrastructure outages, cable health,
 * port-related and chokepoint events. Cross-references with
 * INFRA_NODES from infrastructure-graph config.
 * Click a node → fly to its location.
 */

import { appStore } from '@/core/store';
import { relativeTime } from '@/utils/time';
import { getMap } from '@/map/engine';
import { INFRA_NODES, type InfraNode, type InfraType } from '@/config/infrastructure-graph';
import { SEVERITY_CSS_COLORS } from '@/utils/severity';
import type { NormalizedEvent, Severity } from '@/sources/base-source';

const SUPPLY_SOURCES = new Set(['outages', 'cable-health']);

const INFRA_GROUPS: { label: string; types: InfraType[] }[] = [
  { label: 'Ports', types: ['port'] },
  { label: 'Cables', types: ['cable'] },
  { label: 'Pipelines', types: ['pipeline'] },
  { label: 'Waterways', types: ['waterway'] },
];

function isSupplyChainEvent(ev: NormalizedEvent): boolean {
  if (SUPPLY_SOURCES.has(ev.source)) return true;
  const text = (ev.title + ' ' + (ev.description ?? '')).toLowerCase();
  const tags = ev.tags ?? [];
  if (tags.some(t => t === 'infrastructure' || t === 'supply-chain' || t === 'port' || t === 'cable' || t === 'pipeline')) return true;
  if (ev.type === 'infrastructure') return true;
  if (text.includes('port closure') || text.includes('cable cut') || text.includes('pipeline') || text.includes('chokepoint') || text.includes('strait')) return true;
  return false;
}

function matchNodeToEvent(node: InfraNode, events: NormalizedEvent[]): NormalizedEvent | null {
  const nameLower = node.name.toLowerCase();
  for (const ev of events) {
    const evText = (ev.title + ' ' + (ev.description ?? '')).toLowerCase();
    if (evText.includes(nameLower)) return ev;
    // Check proximity if both have coordinates
    if (ev.location) {
      const dlat = Math.abs(ev.location.lat - node.lat);
      const dlng = Math.abs(ev.location.lng - node.lng);
      if (dlat < 1 && dlng < 1) return ev;
    }
  }
  return null;
}

export function createSupplyChainPanel(): HTMLElement {
  const container = document.createElement('div');
  container.className = 'supply-chain-panel';
  container.style.cssText = 'display: flex; flex-direction: column; height: 100%; overflow-y: auto;';

  const contentArea = document.createElement('div');
  contentArea.style.cssText = 'padding: 12px;';
  container.appendChild(contentArea);

  const cleanupFns: (() => void)[] = [];
  let renderPending = false;

  function scheduleRender(): void {
    if (renderPending) return;
    renderPending = true;
    requestAnimationFrame(() => {
      renderPending = false;
      render();
    });
  }

  function render(): void {
    const events = appStore.getState().events;
    const supplyEvents = events.filter(isSupplyChainEvent);

    contentArea.textContent = '';

    // Find affected infrastructure nodes
    const affectedNodes: { node: InfraNode; event: NormalizedEvent }[] = [];
    for (const node of INFRA_NODES) {
      if (node.type === 'country') continue; // Skip country nodes
      const matchedEvent = matchNodeToEvent(node, supplyEvents);
      if (matchedEvent) {
        affectedNodes.push({ node, event: matchedEvent });
      }
    }

    // Overall health indicator
    const totalInfraNodes = INFRA_NODES.filter(n => n.type !== 'country').length;
    const affectedPct = totalInfraNodes > 0 ? (affectedNodes.length / totalInfraNodes) * 100 : 0;
    const healthColor = affectedPct < 5 ? 'var(--severity-low)' : affectedPct < 15 ? 'var(--severity-medium)' : 'var(--severity-critical)';
    const healthLabel = affectedPct < 5 ? 'Normal' : affectedPct < 15 ? 'Degraded' : 'Critical';

    const summaryHeader = document.createElement('div');
    summaryHeader.style.cssText = `
      margin-bottom: 12px; padding: 10px; border-radius: 6px;
      background: var(--bg-secondary); border: 1px solid var(--border-primary);
    `;

    const healthRow = document.createElement('div');
    healthRow.style.cssText = 'display: flex; align-items: center; gap: 8px; margin-bottom: 6px;';

    const healthDot = document.createElement('span');
    healthDot.style.cssText = `width: 10px; height: 10px; border-radius: 50%; background: ${healthColor};`;
    healthRow.appendChild(healthDot);

    const healthText = document.createElement('span');
    healthText.style.cssText = `font-size: 13px; font-weight: 600; color: ${healthColor};`;
    healthText.textContent = `Supply Chain: ${healthLabel}`;
    healthRow.appendChild(healthText);

    summaryHeader.appendChild(healthRow);

    const statsRow = document.createElement('div');
    statsRow.style.cssText = 'display: flex; gap: 12px; font-size: 10px; color: var(--text-muted);';

    const affectedStat = document.createElement('span');
    affectedStat.textContent = `${affectedNodes.length} affected node${affectedNodes.length !== 1 ? 's' : ''}`;
    statsRow.appendChild(affectedStat);

    const eventStat = document.createElement('span');
    eventStat.textContent = `${supplyEvents.length} event${supplyEvents.length !== 1 ? 's' : ''}`;
    statsRow.appendChild(eventStat);

    summaryHeader.appendChild(statsRow);
    contentArea.appendChild(summaryHeader);

    if (supplyEvents.length === 0 && affectedNodes.length === 0) {
      const msg = document.createElement('div');
      msg.style.cssText = 'font-size: 11px; color: var(--text-muted); font-style: italic; padding: 16px 0; text-align: center;';
      msg.textContent = 'No supply chain disruptions detected';
      contentArea.appendChild(msg);
      return;
    }

    // Group affected nodes by infrastructure type
    for (const group of INFRA_GROUPS) {
      const groupNodes = affectedNodes.filter(an => group.types.includes(an.node.type));

      const sectionHeader = document.createElement('div');
      sectionHeader.style.cssText = `
        font-size: 11px; font-weight: 600; color: var(--text-secondary);
        margin: 10px 0 6px; text-transform: uppercase; letter-spacing: 0.05em;
        display: flex; align-items: center; gap: 6px;
      `;
      sectionHeader.textContent = `${group.label} (${groupNodes.length} affected)`;

      contentArea.appendChild(sectionHeader);

      if (groupNodes.length === 0) {
        const ok = document.createElement('div');
        ok.style.cssText = 'font-size: 10px; color: var(--text-muted); font-style: italic; margin-bottom: 8px;';
        ok.textContent = 'All operational';
        contentArea.appendChild(ok);
        continue;
      }

      // Sort by severity of matched event
      const severityRank: Record<string, number> = { critical: 4, high: 3, medium: 2, low: 1, info: 0 };
      groupNodes.sort((a, b) => {
        const aRank = severityRank[a.event.severity ?? 'info'] ?? 0;
        const bRank = severityRank[b.event.severity ?? 'info'] ?? 0;
        return bRank - aRank;
      });

      for (const { node, event } of groupNodes.slice(0, 20)) {
        const sev = event.severity ?? 'medium';
        const row = document.createElement('div');
        row.style.cssText = `
          padding: 6px 8px; margin-bottom: 4px; border-radius: 4px;
          background: var(--bg-secondary);
          border-left: 3px solid ${SEVERITY_CSS_COLORS[sev as Severity] ?? 'var(--text-muted)'};
          cursor: pointer;
        `;

        row.onclick = () => {
          const map = getMap();
          if (map) {
            map.flyTo({
              center: [node.lng, node.lat],
              zoom: Math.max(map.getZoom(), 6),
              duration: 600,
            });
          }
        };
        row.title = 'Click to fly to location';

        // Node name
        const nameEl = document.createElement('div');
        nameEl.style.cssText = 'font-size: 12px; font-weight: 500; color: var(--text-primary);';
        nameEl.textContent = node.name;
        row.appendChild(nameEl);

        // Event title + time
        const metaRow = document.createElement('div');
        metaRow.style.cssText = 'display: flex; align-items: center; gap: 6px; margin-top: 2px;';

        const evTitle = document.createElement('span');
        evTitle.style.cssText = 'font-size: 10px; color: var(--text-secondary); flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;';
        evTitle.textContent = event.title;
        metaRow.appendChild(evTitle);

        const timeEl = document.createElement('span');
        timeEl.style.cssText = 'font-size: 9px; color: var(--text-muted); flex-shrink: 0;';
        timeEl.textContent = relativeTime(event.timestamp);
        metaRow.appendChild(timeEl);

        row.appendChild(metaRow);

        contentArea.appendChild(row);
      }
    }

    // Also show unmatched supply chain events that didn't match a specific node
    const matchedEventIds = new Set(affectedNodes.map(an => an.event.id));
    const unmatchedEvents = supplyEvents
      .filter(e => !matchedEventIds.has(e.id))
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
      .slice(0, 20);

    if (unmatchedEvents.length > 0) {
      const sectionHeader = document.createElement('div');
      sectionHeader.style.cssText = `
        font-size: 11px; font-weight: 600; color: var(--text-secondary);
        margin: 10px 0 6px; text-transform: uppercase; letter-spacing: 0.05em;
      `;
      sectionHeader.textContent = `Other Disruptions (${unmatchedEvents.length})`;
      contentArea.appendChild(sectionHeader);

      for (const ev of unmatchedEvents) {
        const sev = ev.severity ?? 'info';
        const row = document.createElement('div');
        row.style.cssText = `
          padding: 6px 8px; margin-bottom: 4px; border-radius: 4px;
          background: var(--bg-secondary);
          border-left: 3px solid ${SEVERITY_CSS_COLORS[sev as Severity] ?? 'var(--text-muted)'};
          cursor: ${ev.location ? 'pointer' : 'default'};
        `;

        if (ev.location) {
          row.onclick = () => {
            const map = getMap();
            if (map && ev.location) {
              map.flyTo({
                center: [ev.location.lng, ev.location.lat],
                zoom: Math.max(map.getZoom(), 5),
                duration: 600,
              });
            }
          };
          row.title = 'Click to fly to location';
        }

        const titleEl = document.createElement('div');
        titleEl.style.cssText = 'font-size: 11px; color: var(--text-primary);';
        titleEl.textContent = ev.title;
        row.appendChild(titleEl);

        const timeEl = document.createElement('div');
        timeEl.style.cssText = 'font-size: 9px; color: var(--text-muted); margin-top: 2px;';
        timeEl.textContent = `${ev.source} \u2022 ${relativeTime(ev.timestamp)}`;
        row.appendChild(timeEl);

        contentArea.appendChild(row);
      }
    }
  }

  render();

  cleanupFns.push(
    appStore.subscribe(
      (s) => s.events,
      () => scheduleRender(),
    ),
  );

  (container as HTMLElement & { destroy?: () => void }).destroy = () => {
    for (const fn of cleanupFns) fn();
    cleanupFns.length = 0;
  };

  return container;
}
